import {
  Box,
  Button,
  HStack,
  Icon,
  Image,
  Input,
  Link,
  Switch,
  Text,
  VStack,
} from "@chakra-ui/react";
import { useEffect, useRef, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  IoChatboxOutline,
  IoImageOutline,
  IoLogoWhatsapp,
  IoMailOpenOutline,
} from "react-icons/io5";
import buildingImg from "../../assets/building.png";
import { useTRPC } from "../../trpc/client";
import {
  DesktopPortalPreview,
  MobilePortalPreview,
  PREVIEW_FRAME_WIDTH,
  PREVIEW_HEIGHT,
  PreviewMode,
  PreviewToggle,
} from "./PortalPreview";
import { useMerchant } from "../../hooks/useMerchant";
import { toaster } from "../ui/toaster";
import {
  bumpMediaBust,
  useMediaBust,
  withCacheBust,
} from "../../helpers/mediaUrl";

type ImageKind = "logo" | "dashboard";

const SupportHelpPreview = ({
  email,
  whatsapp,
}: {
  email: string;
  whatsapp: string;
}) => (
  <Box
    width="100%"
    height="100%"
    borderWidth="1px"
    borderColor="gray.200"
    borderRadius="md"
    p={3}
    bg="gray.50"
    fontSize="12px"
    display="flex"
    flexDirection="column"
  >
    <Text fontSize="xs" color="gray.500" mb={2}>
      Vista previa
    </Text>
    <HStack gap={2} align="flex-start" flex="1">
      <Icon as={IoChatboxOutline} boxSize="26px" p={1} flexShrink={0} />
      <VStack align="stretch" gap={2} minWidth={0}>
        <Text>
          <Text fontWeight={700} as="span">
            ¿Necesitas ayuda?
          </Text>{" "}
          Si deseas mayor información sobre el estado actual de tu operación,
          contáctanos en las siguientes vías para atención al cliente:
        </Text>
        <HStack gap={1} align="center" minWidth={0}>
          <Icon as={IoMailOpenOutline} flexShrink={0} />
          <Link
            href={email ? `mailto:${email}` : undefined}
            color={email ? "inherit" : "gray.400"}
            pointerEvents={email ? "auto" : "none"}
          >
            <Text fontWeight={500} truncate>
              {email || "soporte@comercio.com"}
            </Text>
          </Link>
        </HStack>
        <HStack gap={1} align="center" minWidth={0}>
          <Icon as={IoLogoWhatsapp} flexShrink={0} />
          <Link
            href={whatsapp ? `https://wa.me/${whatsapp}` : undefined}
            color={whatsapp ? "inherit" : "gray.400"}
            pointerEvents={whatsapp ? "auto" : "none"}
          >
            <Text fontWeight={500}>
              {whatsapp ? whatsapp.slice(-10) : "3001234567"}
            </Text>
          </Link>
        </HStack>
      </VStack>
    </HStack>
  </Box>
);

const ImageUploadCard = ({
  title,
  description,
  previewUrl,
  placeholder,
  disabled,
  onUpload,
  onClear,
}: {
  title: string;
  description: string;
  previewUrl?: string;
  placeholder?: "logo" | "image";
  disabled?: boolean;
  onUpload: (file: File) => void;
  onClear?: () => void;
}) => {
  const inputRef = useRef<HTMLInputElement>(null);

  return (
    <Box
      flex="1"
      width="100%"
      borderWidth="1px"
      borderColor="gray.200"
      borderRadius="md"
      p={3}
      display="flex"
      alignItems="center"
    >
      <input
        ref={inputRef}
        type="file"
        accept="image/png"
        hidden
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) onUpload(file);
          e.target.value = "";
        }}
      />
      <HStack gap={3} align="center" width="100%">
        <Box
          width="96px"
          height="96px"
          borderRadius="md"
          borderWidth="1px"
          borderColor="gray.200"
          bg="gray.50"
          overflow="hidden"
          flexShrink={0}
          display="flex"
          alignItems="center"
          justifyContent="center"
        >
          {previewUrl ? (
            <Image
              src={previewUrl}
              width="100%"
              height="100%"
              objectFit={placeholder === "logo" ? "contain" : "cover"}
              p={placeholder === "logo" ? 2 : 0}
            />
          ) : (
            <IoImageOutline size={28} color="#A0AEC0" />
          )}
        </Box>
        <VStack align="stretch" gap={1} flex="1" minWidth={0}>
          <Text fontSize="sm" fontWeight={600}>
            {title}
          </Text>
          <Text fontSize="xs" color="gray.500" lineHeight="1.3">
            {description}
          </Text>
          <HStack gap={2} mt={1}>
            <Button
              size="xs"
              variant="outline"
              width="fit-content"
              disabled={disabled}
              onClick={() => inputRef.current?.click()}
            >
              {previewUrl ? "Cambiar" : "Subir"} PNG
            </Button>
            {onClear && previewUrl && (
              <Button
                size="xs"
                variant="ghost"
                disabled={disabled}
                onClick={onClear}
              >
                Quitar
              </Button>
            )}
          </HStack>
        </VStack>
      </HStack>
    </Box>
  );
};

const GeneralTab = (): JSX.Element => {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const { merchant } = useMerchant();

  const [formData, setFormData] = useState({
    logoUrl: "",
    dashboardImageUrl: "",
    supportEmail: "",
    supportWhatsapp: "",
    paymentNotificationsEnabled: false,
  });
  const [pendingFiles, setPendingFiles] = useState<
    Partial<Record<ImageKind, File>>
  >({});
  const pendingUrlsRef = useRef<Partial<Record<ImageKind, string>>>({});
  const [isUploadingImages, setIsUploadingImages] = useState(false);
  const mediaBust = useMediaBust();
  const [previewMode, setPreviewMode] = useState<PreviewMode>("mobile");

  useEffect(() => {
    if (merchant) {
      setFormData({
        logoUrl: merchant.logoUrl || "",
        dashboardImageUrl: merchant.dashboardImageUrl || "",
        supportEmail: merchant.supportEmail || "",
        supportWhatsapp: merchant.supportWhatsapp || "",
        paymentNotificationsEnabled: Boolean(
          merchant.paymentNotificationsEnabled
        ),
      });
    }
  }, [merchant?.id]);

  useEffect(
    () => () => {
      Object.values(pendingUrlsRef.current).forEach(
        (url) => url && URL.revokeObjectURL(url)
      );
    },
    []
  );

  const revokePendingUrl = (kind: ImageKind) => {
    const url = pendingUrlsRef.current[kind];
    if (url) URL.revokeObjectURL(url);
    delete pendingUrlsRef.current[kind];
  };

  const { mutateAsync: getUploadUrl } = useMutation(
    trpc.merchants.getImageUploadUrl.mutationOptions()
  );

  const { mutateAsync: editSettings, isPending: isSaving } = useMutation({
    ...trpc.merchants.editSettings.mutationOptions(),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: trpc.merchants.list.queryKey(),
      });
      toaster.create({
        title: "Cambios guardados",
        description: "La información se ha actualizado con éxito.",
        type: "success",
        duration: 5000,
        meta: { closable: true },
      });
    },
  });

  const handleSelectImage = (kind: ImageKind, file: File) => {
    revokePendingUrl(kind);
    const url = URL.createObjectURL(file);
    pendingUrlsRef.current[kind] = url;
    setPendingFiles((current) => ({ ...current, [kind]: file }));
    setFormData((current) =>
      kind === "logo"
        ? { ...current, logoUrl: url }
        : { ...current, dashboardImageUrl: url }
    );
  };

  const handleClearDashboard = () => {
    revokePendingUrl("dashboard");
    setPendingFiles(({ dashboard: _, ...rest }) => rest);
    setFormData((current) => ({ ...current, dashboardImageUrl: "" }));
  };

  const isFormValid = () =>
    /\S+@\S+\.\S+/.test(formData.supportEmail) &&
    /^\d{11,12}$/.test(formData.supportWhatsapp);

  const uploadPending = async (kind: ImageKind, file: File) => {
    if (!merchant) return;
    const { uploadUrl } = await getUploadUrl({
      rentalsMerchantId: merchant.id,
      kind,
    });
    const response = await fetch(uploadUrl, {
      method: "PUT",
      body: file,
      headers: { "Content-Type": "image/png" },
    });
    if (!response.ok) throw new Error("No se pudo subir la imagen");
  };

  const handleSave = async () => {
    if (!merchant) return;
    const filesToUpload = { ...pendingFiles };
    try {
      setIsUploadingImages(
        Boolean(filesToUpload.logo || filesToUpload.dashboard)
      );
      await Promise.all(
        (["logo", "dashboard"] as const)
          .filter((kind) => filesToUpload[kind])
          .map((kind) => uploadPending(kind, filesToUpload[kind]!))
      );

      const updated = await editSettings({
        rentalsMerchantId: merchant.id,
        supportEmail: formData.supportEmail,
        supportWhatsapp: formData.supportWhatsapp,
        dashboardImage: Boolean(formData.dashboardImageUrl),
        paymentNotificationsEnabled: formData.paymentNotificationsEnabled,
      });

      (["logo", "dashboard"] as const).forEach(revokePendingUrl);
      setPendingFiles({});
      if (filesToUpload.logo || filesToUpload.dashboard) bumpMediaBust();
      setFormData({
        logoUrl: updated.logoUrl || "",
        dashboardImageUrl: updated.dashboardImageUrl || "",
        supportEmail: updated.supportEmail,
        supportWhatsapp: updated.supportWhatsapp,
        paymentNotificationsEnabled: Boolean(
          updated.paymentNotificationsEnabled
        ),
      });
    } catch {
      toaster.create({
        title: "Error",
        description: "Hubo un error al guardar los cambios.",
        type: "error",
        duration: 5000,
        meta: { closable: true },
      });
    } finally {
      setIsUploadingImages(false);
    }
  };

  const isBusy = isSaving || isUploadingImages;

  const logoUrl = withCacheBust(formData.logoUrl, mediaBust);
  const dashboardImageUrl = withCacheBust(
    formData.dashboardImageUrl,
    mediaBust
  );
  const imageSrc = dashboardImageUrl || buildingImg;

  return (
    <VStack gap={6} align="stretch">
      <Box>
        <HStack
          justify="space-between"
          align="flex-start"
          mb={3}
          gap={3}
          minHeight="44px"
        >
          <Box>
            <Text fontWeight={600}>Apariencia del portal</Text>
            <Text fontSize="sm" color="gray.500">
              Así verán tus inquilinos el inicio de sesión. Los cambios se
              aplican al pulsar Guardar.
            </Text>
          </Box>
          <PreviewToggle
            value={previewMode}
            onChange={setPreviewMode}
            disabled={isBusy}
          />
        </HStack>

        <HStack gap={4} align="stretch">
          <Box
            width={`${PREVIEW_FRAME_WIDTH}px`}
            minWidth={`${PREVIEW_FRAME_WIDTH}px`}
            height={`${PREVIEW_HEIGHT}px`}
            flexShrink={0}
            display="flex"
            justifyContent="center"
            alignItems="center"
          >
            {previewMode === "mobile" ? (
              <MobilePortalPreview
                logoUrl={logoUrl || undefined}
                imageSrc={imageSrc}
              />
            ) : (
              <DesktopPortalPreview
                logoUrl={logoUrl || undefined}
                imageSrc={imageSrc}
              />
            )}
          </Box>

          <VStack
            flex="1"
            minWidth={0}
            height={`${PREVIEW_HEIGHT}px`}
            gap={3}
            align="stretch"
          >
            <ImageUploadCard
              title="Logo"
              description="Logo del portal de pagos. Solo PNG."
              previewUrl={logoUrl || undefined}
              placeholder="logo"
              disabled={isBusy}
              onUpload={(file) => handleSelectImage("logo", file)}
            />
            <ImageUploadCard
              title="Imagen de login"
              description="Imágen en el login del portal de pagos. Solo PNG."
              previewUrl={dashboardImageUrl || undefined}
              placeholder="image"
              disabled={isBusy}
              onUpload={(file) => handleSelectImage("dashboard", file)}
              onClear={handleClearDashboard}
            />
          </VStack>
        </HStack>
      </Box>

      <Box>
        <Box mb={3} minHeight="44px">
          <Text fontWeight={600}>Contacto de soporte</Text>
          <Text fontSize="sm" color="gray.500">
            Tus inquilinos usarán estos datos cuando necesiten ayuda
          </Text>
        </Box>
        <HStack gap={4} align="stretch">
          <Box
            width={`${PREVIEW_FRAME_WIDTH}px`}
            minWidth={`${PREVIEW_FRAME_WIDTH}px`}
            flexShrink={0}
          >
            <SupportHelpPreview
              email={formData.supportEmail}
              whatsapp={formData.supportWhatsapp}
            />
          </Box>
          <VStack
            flex="1"
            minWidth={0}
            gap={3}
            align="stretch"
            justify="center"
          >
            <Box>
              <Text fontSize="sm" fontWeight={500} mb={1}>
                Correo de soporte
              </Text>
              <Input
                value={formData.supportEmail}
                onChange={(e) =>
                  setFormData({ ...formData, supportEmail: e.target.value })
                }
                type="email"
                placeholder="soporte@comercio.com"
              />
            </Box>
            <Box>
              <Text fontSize="sm" fontWeight={500} mb={1}>
                WhatsApp de soporte
              </Text>
              <Input
                value={formData.supportWhatsapp}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    supportWhatsapp: e.target.value
                      .replace(/\D/g, "")
                      .slice(0, 12),
                  })
                }
                placeholder="573001234567"
              />
              <Text fontSize="xs" color="gray.500" mt={1}>
                Incluye el indicativo del país, sin espacios (ej. 57…)
              </Text>
            </Box>
          </VStack>
        </HStack>
      </Box>

      <HStack justify="space-between" gap={4}>
        <Box>
          <Text fontWeight={600}>Notificaciones de pagos</Text>
          <Text fontSize="sm" color="gray.500">
            Recibe un correo en{" "}
            {formData.supportEmail || "tu correo de soporte"} cada vez que un
            inquilino pague una factura
          </Text>
        </Box>
        <Switch.Root
          checked={formData.paymentNotificationsEnabled}
          onCheckedChange={({ checked }) =>
            setFormData({ ...formData, paymentNotificationsEnabled: checked })
          }
        >
          <Switch.HiddenInput />
          <Switch.Control>
            <Switch.Thumb />
          </Switch.Control>
        </Switch.Root>
      </HStack>

      <HStack justify="flex-end" pt={1}>
        <Button
          loading={isBusy}
          disabled={!isFormValid() || isBusy}
          onClick={handleSave}
        >
          Guardar
        </Button>
      </HStack>
    </VStack>
  );
};

export default GeneralTab;
